/* eslint-disable no-unused-vars */
import React, {
  useMemo, useState, useEffect, useCallback,
} from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { Typography } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import useQlikApp from 'hooks/useQlikApp';
import useQlikSessionObject from 'hooks/useQlikSessionObject';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import Filter from '../Filter';
import CheckboxFilter from '../CheckboxFilter';
import data from '../../data';

const useStyles = makeStyles((theme) => ({
  resultsList: {
    margin: '50px 0',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  resultsListColumn: {
    '& h3': {
      marginBottom: 20,
    },
    [theme.breakpoints.down('sm')]: {
      flexGrow: 1,
      width: '33%',
      marginBottom: 20,
    },
    [theme.breakpoints.down('xs')]: {
      flexGrow: 1,
      width: '50%',
      marginBottom: 20,
    },
  },
  listItem: {
    position: 'relative',
    display: 'block',
    paddingLeft: 25,
    marginBottom: 5,
    '&::before': {
      display: 'block',
      content: '" "',
      backgroundColor: '#404040',
      width: 16,
      height: 16,
      borderRadius: '50%',
      position: 'absolute',
      top: 2,
      left: 0,
    },
    '&.north-america::before': {
      backgroundColor: '#8A85C6',
    },
    '&.south-america::before': {
      backgroundColor: '#87205D',
    },
    '&.europe::before': {
      backgroundColor: '#10CFC9',
    },
    '&.africa::before': {
      backgroundColor: '#E0BD8D',
    },
    '&.asia::before': {
      backgroundColor: '#AC4D58',
    },
    '&.oceania::before': {
      backgroundColor: '#006580',
    },
  },
  resultsFilters: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  resultsFiltersLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      '& h2': {
        fontSize: '1.85rem',
      },
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: '10px',
      width: '100%',
      order: 2,
      '& h2': {
        fontSize: '1.4rem',
      },
    },
  },
  resultsFiltersRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      order: 1,
      '& .MuiFormControl-root': {
        marginLeft: 0,
      },
    },
  },
  ButtonNav: {
    minWidth: 'auto',
    width: 30,
    height: 30,
    borderRadius: '100%',
    margin: '0 0 0 10px',
    border: '1px solid #00000054',
    '& svg': {
      fontSize: '1rem !important',
    },
  },
  buttonShow: {
    padding: '0 !important',
    '& span': {
      textDecoration: 'underline !important',
      textTransform: 'lowercase !important',
    },
  },
}));

const Results = ({
  vizRef, medalsState, handleMedalsStateChange, width, lang,
}) => {
  const qlikApp = useQlikApp();

  const [measure, setMeasure] = useState({
    qVal: '',
    sortExp: '',
  });
  const [expand, setExpand] = useState({
    expanded: false,
    itemsToShow: 5,
    list: '',
  });

  const continents = ['North America', 'South America', 'Europe', 'Africa', 'Asia', 'Oceania'];

  /* ----  Get Data ---- */
  const qSessionData = useQlikSessionObject({
    app: qlikApp,
    properties: {
      qHyperCubeDef: {
        qDimensions: [
          {
            qDef: {
              qFieldDefs: ['[Team]'],
              qFieldLabels: ['Team'],
              qSortCriterias: [
                {
                  qSortByExpression: 1,
                  qExpression: {
                    qv: measure.sortExp ? `Rank(${measure.sortExp})` : 'Rank( (Sum({<[Team]=>} [Gold]) * 1000) + (Sum({<[Team]=>} [Silver]) * 10) + (Sum({<[Team]=>} [Bronze])))',
                  },
                },
              ],
              autoSort: false,
            },
          },
          {
            qDef: { qFieldDefs: ['Continent'], qFieldLabels: ['Continent'] },
          },
        ],
        qMeasures: [
          {
            qDef: {
              qDef: `${measure.qVal || 'Sum({<[Team]=>} [Total])'}`,
              qLabel: 'Medals',
            },
            qAttributeExpressions: [
              {
                qExpression: '=Sum({<[Team]=>} [Gold])',
              },
              {
                qExpression: '=Sum({<[Team]=>} [Silver])',
              },
              {
                qExpression: '=Sum({<[Team]=>} [Bronze])',
              },
            ],
          },
        ],
      },
    },
  });

  const _data = useMemo(() => {
    if (!qSessionData.layout) return [];
    return qSessionData.layout.qHyperCube.qDataPages[0].qMatrix.map((row) => ({
      team: row[0].qText,
      continent: row[1].qText,
      medals: row[2].qText,
      medalsGold: row[2].qAttrExps.qValues[0].qText,
      medalsSilver: row[2].qAttrExps.qValues[1].qText,
      medalsBronze: row[2].qAttrExps.qValues[2].qText,
    }));
  }, [qSessionData.layout]);

  const groupBy = (key) => (array) => array.reduce((objectsByKeyValue, obj) => {
    const value = obj[key];
    objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
    return objectsByKeyValue;
  }, {});
  const groupedByContinent = groupBy('continent')(_data);
  /* -------------------------------- */

  /* ----  Get Years for prev/next ---- */
  const qFilter = useQlikSessionObject({
    app: qlikApp,
    properties: {
      qListObjectDef: {
        qDef: {
          qFieldDefs: ['[YearCity]'],
          qFieldLabels: ['YearCity'],
        },
        qFrequencyMode: 'V',
      },
    },
  });

  const years = useMemo(() => {
    if (!qFilter.layout) return [];
    return qFilter.layout.qListObject.qDataPages[0].qMatrix.map((row) => ({
      qElemNumber: row[0].qElemNumber,
      qState: row[0].qState,
      qText: row[0].qText,
    }));
  }, [qFilter]);

  useEffect(() => {
    if (!qFilter.model) return;
    qFilter.model.selectListObjectValues('/qListObjectDef', [27], false);
  }, [qFilter.model]);

  const selectedYear = useMemo(() => {
    if (!qFilter.layout) return [];
    return qFilter.layout.qListObject.qDataPages[0].qMatrix
      .filter((row) => row[0].qState === 'S')
      .map((row) => ({ year: row[0].qElemNumber, yearName: row[0].qText }));
  }, [qFilter.layout]);

  const handleBack = useCallback((event) => {
    if (!qFilter.layout) return;
    qFilter.model.selectListObjectValues('/qListObjectDef', [selectedYear[0].year - 1], false);
  }, [qFilter.layout]);

  const handleNext = useCallback((event) => {
    if (!qFilter.layout) return;
    qFilter.model.selectListObjectValues('/qListObjectDef', [selectedYear[0].year + 1], false);
  }, [qFilter.layout]);
  /* -------------------------------- */

  const handleCb = (dt) => {
    const qVal = dt.map((d) => (`Sum({<[Team]=>} [${d}])`)).join(' + ');
    const sortExp = dt.map((d) => {
      let multi = 1;
      if (d === 'Gold') { multi = 1000; } else if (d === 'Silver') { multi = 10; } else { multi = 1; }
      return (`Sum({<[Team]=>} [${d}]) * ${multi}`);
    }).join(' + ');
    setMeasure({
      qVal,
      sortExp,
    });
  };

  const showMore = (listName, listItems) => {
    if (expand.itemsToShow === 5) {
      setExpand({ itemsToShow: listItems.length, expanded: true, list: listName });
    } else {
      setExpand({ itemsToShow: 5, expanded: false, list: listName });
    }
  };

  const MedalsTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: 'rgba(64,64,64,.95)',
      color: '#FFFFFF',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      borderRadius: 4,
    },
  }))(Tooltip);

  const classes = useStyles();
  return (
    <>
      <Box className={classes.resultsFilters}>
        <Box className={classes.resultsFiltersLeft}>
          <Typography variant="h2">{selectedYear[0] && selectedYear[0].yearName}</Typography>
          <Box>
            <Button
              classes={{ root: classes.ButtonNav }}
              onClick={handleBack}
              disabled={selectedYear[0] && selectedYear[0].year === 0}
            >
              <ArrowBackIosIcon style={{ position: 'relative', left: '3px' }} />
            </Button>
            <Button
              classes={{ root: classes.ButtonNav }}
              onClick={handleNext}
              disabled={selectedYear[0] && selectedYear[0].year === 27}
            >
              <ArrowForwardIosIcon />
            </Button>
          </Box>
        </Box>
        <Box className={classes.resultsFiltersRight}>
          <CheckboxFilter ref={vizRef} parentCallback={handleCb} medalsState={medalsState} handleMedalsStateChange={handleMedalsStateChange} />
          <Filter field="Sport" label={data.en.filters.Sport} lang={lang} color="light" />
        </Box>
      </Box>
      <Box className={classes.resultsList}>
        {
          continents.map((continent) => {
            const slugifiedContinent = continent.toLowerCase().replace(/ +/g, '-');
            return (
              groupedByContinent[continent] && (
                <Box key={slugifiedContinent} className={classes.resultsListColumn}>
                  {!isWidthUp('md', width) && <Typography variant="h3">{continent}</Typography>}
                  {
                    (!isWidthUp('md', width))
                      ? groupedByContinent[continent]
                        .slice(0, (expand.list === slugifiedContinent) ? expand.itemsToShow : 5)
                        .map((d) => (
                          <MedalsTooltip
                            title={(
                              <>
                                <Typography color="inherit" style={{ fontSize: '12px' }}>{`${d.team}`}</Typography>
                                <Typography color="inherit" style={{ fontSize: '14px' }}>{`${d.medals} ${d.medals === 1 ? 'medal' : 'medals'}`}</Typography>
                                <Typography color="inherit" style={{ fontSize: '14px' }}>{`🥇 ${d.medalsGold} 🥈 ${d.medalsSilver} 🥉 ${d.medalsBronze}`}</Typography>
                              </>
                            )}
                            key={d.team}
                          >
                            <Typography variant="body2" className={`${classes.listItem} ${slugifiedContinent}`}>{`${d.medals} ${d.team}`}</Typography>
                          </MedalsTooltip>
                        ))
                      : groupedByContinent[continent]
                        .map((d) => (
                          <MedalsTooltip
                            title={(
                              <>
                                <Typography color="inherit" style={{ fontSize: '12px' }}>{`${d.team}`}</Typography>
                                <Typography color="inherit" style={{ fontSize: '14px' }}>{`${d.medals} ${d.medals === 1 ? 'medal' : 'medals'}`}</Typography>
                                <Typography color="inherit" style={{ fontSize: '14px' }}>{`🥇 ${d.medalsGold} 🥈 ${d.medalsSilver} 🥉 ${d.medalsBronze}`}</Typography>
                              </>
                            )}
                            key={d.team}
                          >
                            <Typography variant="body2" className={`${classes.listItem} ${slugifiedContinent}`}>{`${d.medals} ${d.team}`}</Typography>
                          </MedalsTooltip>
                        ))
                  }
                  {
                    (groupedByContinent[continent].length > 5 && !isWidthUp('md', width)) && (
                      <Button className={classes.buttonShow} onClick={() => showMore(slugifiedContinent, groupedByContinent[continent])}>
                        {
                          expand.expanded && (expand.list === slugifiedContinent)
                            ? (<span>{lang === 'jp' ? data.jp.results.showLess : data.en.results.showLess}</span>)
                            : (<span>{lang === 'jp' ? data.jp.results.showMore : data.en.results.showMore}</span>)
                        }
                      </Button>
                    )
                  }
                </Box>
              )
            );
          })
        }
      </Box>
    </>
  );
};

Results.propTypes = {
  vizRef: PropTypes.object.isRequired,
  medalsState: PropTypes.object.isRequired,
  handleMedalsStateChange: PropTypes.func.isRequired,
  width: PropTypes.string,
  lang: PropTypes.string,
};
Results.defaultProps = {
  width: 'sm',
  lang: 'en',
};

export default withWidth()(Results);
